import React from "react";
import { ComboboxOption, ComboboxOptions, Transition } from "@headlessui/react";
import { Check } from "lucide-react";
import { Text } from "../../../utils/text/text";
// Anchored, not absolute — absolute got clipped by a modal's scroll ancestor.
const ANCHOR = { to: "bottom start", gap: 6 };
// Cap goes through --anchor-max-height (HeadlessUI sets it inline) — a max-h-* class would lose to that.
const OPTIONS_CLASS = "z-40 [--anchor-max-height:15rem] w-[var(--input-width)] origin-top overflow-auto overscroll-contain rounded-2xl border border-line bg-surface py-1.5 text-base shadow-overlay focus:outline-none sm:text-sm";
const OPTION_CLASS = "group relative mx-1.5 flex h-11 sm:h-9 w-[calc(100%-0.75rem)] cursor-pointer select-none items-center rounded-lg pl-9 pr-3 text-gray-800 data-[focus]:bg-selected data-[focus]:text-brand-700 data-[selected]:bg-selected data-[selected]:text-brand-700";
// Virtual mode's stale render still calls children as a function; a plain-element branch crashed, so empty state is a sentinel option.
export const NO_RESULT_OPTION = " no-result";
export const toVirtualOptions = (filteredList) => filteredList.length === 0 ? [NO_RESULT_OPTION] : filteredList.map((i) => i.value);
// Must be virtual.disabled, or defaultToFirstOption lets Enter commit the sentinel as the value.
export const isNoResultOption = (option) => option === NO_RESULT_OPTION;
export const normalizeSearch = (s) => s
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
export const filterItems = (items, search) => {
    if (search === "")
        return items;
    const query = normalizeSearch(search);
    return items.filter((i) => normalizeSearch(i.name).includes(query));
};
const HighlightedName = ({ name, search }) => {
    if (!search)
        return <>{name}</>;
    const normalizedName = normalizeSearch(name);
    // Rare non-NFC edge: normalization changed the string length, indices no longer map — skip highlight.
    if (normalizedName.length !== name.length)
        return <>{name}</>;
    const normalizedSearch = normalizeSearch(search);
    const idx = normalizedName.indexOf(normalizedSearch);
    if (idx === -1)
        return <>{name}</>;
    return (<>
            {name.slice(0, idx)}
            <span className="font-semibold text-brand-700">{name.slice(idx, idx + normalizedSearch.length)}</span>
            {name.slice(idx + normalizedSearch.length)}
        </>);
};
/** The dropdown panel shared by Select and SelectMultiple. */
export const SelectListOptions = ({ search, setSearch, items }) => {
    // Built from the full list — HeadlessUI's stale render still returns already-filtered-out options needing their label.
    const nameMap = new Map(items.map((i) => [i.value, i.name]));
    return (<Transition enter="transition-[opacity,translate,scale] ease-out-soft duration-150" enterFrom="opacity-0 -translate-y-1 scale-[0.98]" enterTo="opacity-100 translate-y-0 scale-100" leave="transition-[opacity,translate,scale] ease-out-soft duration-100" leaveFrom="opacity-100 translate-y-0 scale-100" leaveTo="opacity-0 -translate-y-1 scale-[0.98]" afterLeave={() => setSearch("")}>
            <ComboboxOptions as={"div"} anchor={ANCHOR} className={OPTIONS_CLASS}>
                {({ option: v }) => v === NO_RESULT_OPTION ? (<div className="w-full cursor-default select-none py-2 px-4 text-gray-500">
                            <Text id={"empty"}/>
                        </div>) : (<ComboboxOption value={v} className={OPTION_CLASS}>
                            <span className="block truncate group-data-[selected]:font-medium">
                                <HighlightedName name={nameMap.get(v) || "-"} search={search}/>
                            </span>
                            <span className="absolute inset-y-0 left-0 hidden items-center pl-2.5 group-data-[selected]:flex">
                                <Check className="h-4 w-4" aria-hidden="true"/>
                            </span>
                        </ComboboxOption>)}
            </ComboboxOptions>
        </Transition>);
};
//# sourceMappingURL=selectListOptions.jsx.map